/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { CommonModule } from '@angular/common';
import { Component, ViewEncapsulation, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslatePipe } from '@ngx-translate/core';
import { PersonRef, WorkflowService, WorkflowTemplate } from './workflow.service';

export interface WorkflowVisibilityDialogData {
  template: WorkflowTemplate;
}

/**
 * The catalogue's quick visibility editor — the same gate the designer's
 * canvas exposes, reachable without leaving Settings. Edits a copy of the
 * template and persists through {@link WorkflowService.saveTemplate}, which
 * keeps the shared repository document authoritative.
 */
@Component({
  selector: 'app-workflow-visibility-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatAutocompleteModule,
    MatButtonModule,
    MatDialogModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
    TranslatePipe
  ],
  template: `
    <h2 mat-dialog-title>
      <mat-icon class="aca-visdlg__titleicon">visibility_lock</mat-icon>
      {{ data.template.name }}
    </h2>
    <mat-dialog-content class="aca-visdlg">
      <label class="aca-visdlg__check">
        <input type="checkbox" [(ngModel)]="tpl.restricted" />
        <span>{{ 'DMS.BUILDER.VIS_RESTRICT' | translate }}</span>
      </label>
      <p class="aca-visdlg__hint" *ngIf="tpl.restricted">{{ 'DMS.BUILDER.VIS_HINT' | translate }}</p>

      <ng-container *ngIf="tpl.restricted">
        <div class="aca-visdlg__group" *ngFor="let kind of kinds">
          <span class="aca-visdlg__label">
            <mat-icon>{{ kind === 'group' ? 'groups' : 'person' }}</mat-icon>
            {{ (kind === 'group' ? 'DMS.BUILDER.VIS_GROUPS' : 'DMS.BUILDER.VIS_USERS') | translate }}
          </span>
          <span class="aca-visdlg__chip" *ngFor="let p of listFor(kind); let i = index">
            {{ p.displayName }}
            <button type="button" class="aca-visdlg__chipx" (click)="remove(kind, i)" [attr.aria-label]="'DMS.UI.55C866' | translate">
              <mat-icon>close</mat-icon>
            </button>
          </span>
          <mat-form-field appearance="outline" class="aca-visdlg__field">
            <mat-label>{{ (kind === 'group' ? 'DMS.BUILDER.VIS_ADD_GROUP' : 'DMS.BUILDER.VIS_ADD_USER') | translate }}</mat-label>
            <input
              matInput
              [ngModel]="termFor(kind)"
              (ngModelChange)="search(kind, $event)"
              [matAutocomplete]="kind === 'group' ? autoGroup : autoUser"
            />
            <mat-autocomplete #autoUser="matAutocomplete" (optionSelected)="add('user', $event.option.value)">
              <mat-option *ngFor="let r of userResults()" [value]="r">{{ r.displayName }}</mat-option>
            </mat-autocomplete>
            <mat-autocomplete #autoGroup="matAutocomplete" (optionSelected)="add('group', $event.option.value)">
              <mat-option *ngFor="let r of groupResults()" [value]="r">{{ r.displayName }}</mat-option>
            </mat-autocomplete>
          </mat-form-field>
        </div>
      </ng-container>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button (click)="ref.close()">{{ 'DMS.UI.7D3094' | translate }}</button>
      <button mat-flat-button color="primary" (click)="save()" [disabled]="busy()">{{ 'DMS.UI.6EA45A' | translate }}</button>
    </mat-dialog-actions>
    <mat-progress-bar mode="indeterminate" *ngIf="busy()" />
  `,
  encapsulation: ViewEncapsulation.None,
  styles: [
    `
      .aca-visdlg {
        min-width: 440px;
      }
      .aca-visdlg__titleicon {
        vertical-align: -5px;
        margin-right: 6px;
        font-size: 20px;
        width: 20px;
        height: 20px;
      }
      .aca-visdlg__check {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 14px;
        cursor: pointer;
      }
      .aca-visdlg__hint {
        font-size: 12.5px;
        color: var(--theme-secondary-text, rgba(0, 0, 0, 0.55));
        margin: 6px 0 14px;
      }
      .aca-visdlg__group {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        margin-bottom: 10px;
      }
      .aca-visdlg__label {
        display: inline-flex;
        align-items: center;
        gap: 5px;
        font-size: 13px;
        font-weight: 600;
        min-width: 86px;
        color: var(--theme-secondary-text, rgba(0, 0, 0, 0.6));
      }
      .aca-visdlg__label mat-icon {
        font-size: 17px;
        width: 17px;
        height: 17px;
      }
      .aca-visdlg__chip {
        display: inline-flex;
        align-items: center;
        gap: 2px;
        padding: 3px 4px 3px 10px;
        border-radius: 14px;
        background: var(--aca-selected-background, #eef2ff);
        font-size: 12.5px;
      }
      .aca-visdlg__chipx {
        border: 0;
        background: none;
        cursor: pointer;
        padding: 0;
        display: inline-flex;
      }
      .aca-visdlg__chipx mat-icon {
        font-size: 15px;
        width: 15px;
        height: 15px;
      }
      .aca-visdlg__field {
        flex: 1;
        min-width: 180px;
      }
      .aca-visdlg__field .mat-mdc-form-field-subscript-wrapper {
        display: none;
      }
    `
  ]
})
export class WorkflowVisibilityDialogComponent {
  readonly data = inject<WorkflowVisibilityDialogData>(MAT_DIALOG_DATA);
  readonly ref = inject(MatDialogRef<WorkflowVisibilityDialogComponent>);
  private readonly wf = inject(WorkflowService);

  readonly kinds: ('user' | 'group')[] = ['user', 'group'];
  /** Working copy — cancelled edits never touch the catalogue. */
  readonly tpl: WorkflowTemplate = {
    ...this.data.template,
    allowedUsers: [...(this.data.template.allowedUsers ?? [])],
    allowedGroups: [...(this.data.template.allowedGroups ?? [])]
  };

  readonly busy = signal(false);
  readonly userResults = signal<PersonRef[]>([]);
  readonly groupResults = signal<PersonRef[]>([]);
  private userTerm = '';
  private groupTerm = '';

  listFor(kind: 'user' | 'group'): PersonRef[] {
    return kind === 'group' ? (this.tpl.allowedGroups ?? []) : (this.tpl.allowedUsers ?? []);
  }

  termFor(kind: 'user' | 'group'): string {
    return kind === 'group' ? this.groupTerm : this.userTerm;
  }

  search(kind: 'user' | 'group', term: string): void {
    if (kind === 'group') {
      this.groupTerm = term;
      this.wf.searchGroups(term).subscribe((r) => this.groupResults.set(r));
    } else {
      this.userTerm = term;
      this.wf.searchPeople(term).subscribe((r) => this.userResults.set(r));
    }
  }

  add(kind: 'user' | 'group', person: PersonRef): void {
    if (!person?.id) {
      return;
    }
    const list = kind === 'group' ? (this.tpl.allowedGroups ??= []) : (this.tpl.allowedUsers ??= []);
    if (!list.some((x) => x.id === person.id)) {
      list.push(person);
    }
    if (kind === 'group') {
      this.groupTerm = '';
    } else {
      this.userTerm = '';
    }
  }

  remove(kind: 'user' | 'group', index: number): void {
    this.listFor(kind).splice(index, 1);
  }

  save(): void {
    if (this.busy()) {
      return;
    }
    this.busy.set(true);
    this.wf.saveTemplate(this.tpl).subscribe({
      next: () => this.ref.close(true),
      error: () => {
        this.busy.set(false);
        this.ref.close(false);
      }
    });
  }
}
